/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2022 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useEffect } from "react";
import { Box, Typography, Button, MenuItem, Tabs, Tab } from "@mui/material";
import dayjs from "dayjs";
import { useMutation } from "@tanstack/react-query";
import {
  getUtilityListQuery,
  getIsoListQuery,
  getGasUtilitiesListQuery,
  getSiteSettingsMutation,
  deleteOrganizationMutation,
  postSiteSettingsMutation,
  getSiteMetaDataAssembliesQuery,
  getManufacturersListQuery,
} from "../../lib/Queries";
import countries from "../../lib/Countries";
import { siteData, assetsTypeList } from "../../lib/siteData";
import { LoadingButton } from "@mui/lab";
import timezones from "../../lib/Timezones";
import sgipOptionsList from "../../lib/sgipCategories";
import states from "../../lib/States";
import PropTypes from "prop-types";
import DeleteSiteModal from "./SiteDeleteModal";
import { useTheme } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { getPowerOutput, getEnergyOutput } from "../Site/SiteSpecs";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AssetSetupModal from "./assetModal";
import AssetPreFillModal from "./preFillAssetsModal";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useNavigate, useSearchParams } from "react-router-dom";
import NorthWestOutlined from "@mui/icons-material/NorthWestOutlined";
import { useSnackbar } from "notistack";
import SiteInfo from "./SiteInfo";
import SiteAssets from "./SiteAssets";
import useSiteFormState from "./hooks/useSiteFormState";
import useAssetsData from "./hooks/useAssetsData";
import SiteAlarmActions from "./SiteAlarmActions";
import SiteIntegrations from "./SiteIntegrations";

const styles = {
  caption: {
    color: "rgba(0, 0, 0, 0.60)",
    marginBottom: "1rem",
  },
  settingsPanel: {
    width: "100%",
    py: "1rem",
    px: "2rem",
  },
  settingsNewPanel: {
    width: "100%",
    py: "3rem",
  },
  subtitle: {
    color: "rgba(0, 0, 0, 0.87)",
    fontWeight: "700",
  },
  genericTab: {
    backgroundColor: "#fff",
    border: "1px solid #E1E2E4",
    borderRadius: "5px",
    padding: "2rem",
    width: "184px",
    height: "151px",
    justifyContent: "center",
    fontSize: 12,
  },
  genericTabLabel: {
    fontSize: 14,
  },
};

const SiteSettings = ({ OrganizationUuid, site, onClose, hideTabs }) => {
  const {
    statusList,
    contractTypesList,
    dealStructuresList,
    billSavingsProvidersList,
    gridServicesProvidersList,
  } = siteData;
  const theme = useTheme();
  const [currentSite, setSite] = useState(site);
  useEffect(() => {
    if (site?.upkeep_location_id !== currentSite?.upkeep_location_id) {
      setSite((prev) => ({
        ...prev,
        upkeep_location_id: site?.upkeep_location_id,
      }));
    }
  }, [site?.upkeep_location_id]);
  const [dirty, setDirty] = useState(false);
  const [name, setName] = useState(currentSite?.name || null);
  const [siteState, setSiteState] = useState(currentSite?.state || null);
  const [iso, setIso] = useState(currentSite?.iso || null);
  const [gasUtility, setGasUtility] = useState(currentSite?.gas_utility || null);
  const [utility, setUtility] = useState(currentSite?.Utility || null);
  const [timezone, setTimezone] = useState(currentSite?.timezone || null);
  const [address1, setAddress1] = useState(currentSite?.address?.address1 || null);
  const [city, setCity] = useState(currentSite?.address?.city || null);
  const [zip, setZip] = useState(currentSite?.address?.zip || null);
  const [usState, setUsState] = useState(currentSite?.address?.state || null);
  const [country, setCountry] = useState(currentSite?.address?.country || "United States");
  const [latitude, setLatitude] = useState(currentSite?.latitude || null);
  const [longitude, setLongitude] = useState(currentSite?.longitude || null);
  const [siteNotifications, setSiteNotifications] = useState(
    currentSite?.send_notifications || currentSite?.uuid === undefined,
  );
  const [hasLoad, setHasLoad] = useState(currentSite?.has_load || null);
  const [contractType, setContractType] = useState(currentSite?.contract_type || []);
  const [dealStructure, setDealStructure] = useState(currentSite?.deal_structure || []);
  const [billSavingsProvider, setBillSavingsProvider] = useState(
    currentSite?.bill_savings_provider || null,
  );
  const [gridServicesProvider, setGridServicesProvider] = useState(
    currentSite?.grid_services_provider || null,
  );
  const [commissionedDate, setCommissionedDate] = useState(
    currentSite?.commissioned_date ? new dayjs(currentSite.commissioned_date) : null,
  );
  const [contractEndDate, setContractEndDate] = useState(
    currentSite?.contract_end_date ? new dayjs(currentSite.contract_end_date) : null,
  );
  const [dateAcquired, setDateAcquired] = useState(
    currentSite?.date_acquired ? new dayjs(currentSite.date_acquired) : null,
  );
  const [sgipBudgetCategory, setSgipBudgetCategory] = useState(
    currentSite?.sgip_budget_category || null,
  );
  const [sgipStartDate, setSgipStartDate] = useState(
    currentSite?.sgip_start_date ? new dayjs(currentSite.sgip_start_date) : null,
  );
  const [solarCapacity, setSolarCapacity] = useState(currentSite?.solar_capacity || null);
  const [batteryCapacity, setBatteryCapacity] = useState(currentSite?.battery_capacity || null);
  const [filteredRows, setFilteredRows] = useState({});
  const [isDataReady, setIsDataReady] = useState(false);
  const [setupRows, setSetupRows] = useState({});
  const [isSetupDataReady, setIsSetupDataReady] = useState(false);
  const [batteryEnergyCapacity, setBatteryEnergyCapacity] = useState(
    currentSite?.battery_energy_capacity || null,
  );
  const [gensetCapacity, setGensetCapacity] = useState(currentSite?.genset_capacity || null);
  const [edgeId, setEdgeId] = useState(currentSite?.group_id || null);
  const [sgipCapacity, setSgipCapacity] = useState(currentSite?.sgip_capacity || null);
  const [postTariffSame, setPostTariffSame] = useState(true);
  const [open, setOpen] = useState(false); // State to control modal visibility
  const [isPreFilledModalOpen, setIsPreFilledModalOpen] = useState(false);
  const [isAssetModalOpen, setIsAssetModalOpen] = useState(false); // asset Modal
  const [selectedAsset, setSelectedAsset] = useState(null);
  const isLoading = false;
  const error = false;
  const scaleosType = "All";
  const { siteSpec4 } = useFlags();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const VALID_TABS = ["details", "assets", "integrations", "alarms"];
  const tabFromUrl = searchParams.get("tab");
  const activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "details";

  const setActiveTab = (tab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === "details") {
          next.delete("tab");
        } else {
          next.set("tab", tab);
        }
        return next;
      },
      { replace: true },
    );
  };
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const {
    data: isoList,
    isLoading: isoListIsLoading,
    refetch: refetchIsoList,
  } = useQuery({
    ...getIsoListQuery(),
  });
  const {
    data: gasUtilitiesList,
    isLoading: gasUtilitiesListIsLoading,
    refetch: refetchGasUtilitiesList,
  } = useQuery({
    ...getGasUtilitiesListQuery(),
  });
  const {
    data: utilityList,
    isLoading: utilityListIsLoading,
    refetch: refetchUtilityList,
  } = useQuery({
    ...getUtilityListQuery(false),
  });

  const {
    data: assembliesList,
    isLoading: isAssembliesLoading,
    refetch: refetchAssembliesList,
  } = useQuery(currentSite?.uuid ? getSiteMetaDataAssembliesQuery(currentSite.uuid) : () => null, {
    enabled: !!currentSite?.uuid,
  });
  const {
    data: manufacturersList,
    isLoading: manufacturersListIsLoading,
    refetch: refetchManufacturersList,
  } = useQuery({
    ...getManufacturersListQuery(scaleosType),
  });

  const mutation = useMutation({
    ...getSiteSettingsMutation(),
  });

  const mutation_delete = useMutation({
    ...deleteOrganizationMutation(),
  });

  const mutation_new_site = useMutation({
    ...postSiteSettingsMutation(),
  });

  const handleSaveAndClose = () => {
    const newSettings = {
      uuid: currentSite?.uuid,
      name: name,
      state: siteState,
      latitude: latitude,
      longitude: longitude,
      timezone: timezone,
      iso: iso,
      gas_utility: gasUtility,
      Utility: utility,
      send_notifications: siteNotifications,
      organization_uuid: OrganizationUuid,
      address: {
        address1: address1,
        city: city,
        state: usState,
        zip: zip,
        country: country,
      },
      contract_type: contractType,
      deal_structure: dealStructure,
      bill_savings_provider: billSavingsProvider,
      grid_services_provider: gridServicesProvider,
      commissioned_date: commissionedDate,
      contract_end_date: contractEndDate,
      date_acquired: dateAcquired,
      sgip_budget_category: sgipBudgetCategory,
      sgip_start_date: sgipStartDate,
      solar_capacity: Number(solarCapacity) || null,
      battery_capacity: Number(batteryCapacity) || null,
      battery_energy_capacity: Number(batteryEnergyCapacity) || null,
      genset_capacity: Number(gensetCapacity) || null,
      group_id: edgeId,
      sgip_capacity: Number(sgipCapacity) || null,
      has_load: hasLoad,
    };

    if (currentSite?.uuid) {
      mutation.mutate(newSettings, {
        onSuccess: () => {
          setDirty(false);
          setSite(newSettings);
          refetchAssembliesList();
          // Ensure org sites list is fresh when navigating back to this site
          if (OrganizationUuid) {
            queryClient.invalidateQueries({
              queryKey: ["organization-sites-list-" + OrganizationUuid],
            });
          }
          if (currentSite?.uuid) {
            queryClient.invalidateQueries({ queryKey: ["site-metadata-" + currentSite.uuid] });
          }
          if (edgeId && edgeId !== currentSite?.group_id) {
            const duration = 3000;
            enqueueSnackbar("Edge Group ID saved. The page will refresh in 3 seconds.", {
              autoHideDuration: duration,
              variant: "success",
            });
            setTimeout(() => {
              window.location.reload();
            }, duration);
          }
        },
      });
    } else {
      mutation_new_site.mutate(newSettings, {
        onSuccess: () => {
          setDirty(false);
          onClose();
          refetchAssembliesList();
          if (OrganizationUuid) {
            queryClient.invalidateQueries({
              queryKey: ["organization-sites-list-" + OrganizationUuid],
            });
          }
        },
      });
    }
  };

  const handleClick = () => {
    navigate(`/site/${name.replace(/ /g, "-")}`);
  };

  const allFieldsFilled = () => {
    return name && address1 && city && siteState && usState && zip && timezone && utility && iso;
  };

  const timezonesOptions = timezones.map((zone) => ({
    label: zone,
    value: zone,
  }));

  const sgipOptions = sgipOptionsList.map((sgip) => ({
    label: sgip,
    value: sgip,
  }));

  const statesOptions = Object.keys(states).map((stateAbbreviation) => ({
    label: stateAbbreviation,
    value: stateAbbreviation,
  }));

  const getCountries = () => {
    return countries.map((country) => (
      <MenuItem key={country} value={country}>
        {country}
      </MenuItem>
    ));
  };

  const getStatus = () => {
    return statusList.map((status) => (
      <MenuItem key={status} value={status}>
        {status}
      </MenuItem>
    ));
  };
  const getContractTypes = () => {
    return contractTypesList.map((contract) => (
      <MenuItem key={contract} value={contract}>
        {contract}
      </MenuItem>
    ));
  };
  const getDealStructures = () => {
    return dealStructuresList.map((deal) => (
      <MenuItem key={deal} value={deal}>
        {deal}
      </MenuItem>
    ));
  };
  const getbillSavingsProviders = () => {
    return billSavingsProvidersList.map((provider) => (
      <MenuItem key={provider} value={provider}>
        {provider}
      </MenuItem>
    ));
  };
  const getServicesProviders = () => {
    return gridServicesProvidersList.map((provider) => (
      <MenuItem key={provider} value={provider}>
        {provider}
      </MenuItem>
    ));
  };

  const handleDelete = () => {
    setOpen(true); // Open the confirmation modal
  };

  const confirmDelete = () => {
    mutation_delete.mutate(currentSite?.uuid, {
      onSuccess: () => {
        console.log("Site deleted successfully");
        setOpen(false); // Close the modal on success
      },
      onError: (error) => {
        console.error("Error deleting site:", error);
        setOpen(false); // Close the modal on error
      },
    });
  };

  useEffect(() => {
    if (isAssembliesLoading || manufacturersListIsLoading) {
      setSetupRows({});
      setIsSetupDataReady(false);
      return;
    }
    const resolveSetupRows = async () => {
      const rows = Array.isArray(assembliesList)
        ? assembliesList
            .filter(
              (assembly) =>
                assembly?.uuid &&
                (assembly.scaleos_type == null ||
                  (assembly.power_capacity == null &&
                    !["Utility", "Controller"].includes(assembly.scaleos_type))),
            )
            .map((assembly) => ({
              id: assembly.uuid,
              name: assembly.name || "Unknown",
              source: assembly.source_info?.[0]?.source || "N/A",
              lastUpdate:
                assembly.last_update && dayjs(assembly.last_update).isValid()
                  ? dayjs(assembly.last_update).format("MMM D YYYY, h:mm A")
                  : assembly.last_update,
              scaleosName: assembly.scaleos_name,
              manufacturer: assembly.manufacturer,
              manufacturerName:
                manufacturersList.find((m) => m.uuid === assembly.manufacturer)?.name || null,
              model: assembly.model,
              modelName:
                manufacturersList
                  .find((m) => m.uuid === assembly.manufacturer)
                  ?.model.find((model) => model.uuid === assembly.model)?.name || null,
              outputCapacity: assembly.power_capacity,
              storageCapacity: assembly.energy_capacity,
              scaleosType: assembly.scaleos_type,
              serialNumber: assembly.serial_number,
              solarMounting: assembly.mounting,
              showInScale: assembly.weight,
              enableAlarms: assembly.trigger_alarms,
              gensetUse: assembly.genset_use,
              runHoursLimit: assembly.run_hours_limit,
              sourceInfo: assembly.source_info,
              sources: assembly.sources,
              edgeId: edgeId,
              parentAsset: assembly.parent_asset ?? null,
              timeSeries: assembly.time_series || [],
              sourceName: assembly.source_name,
            }))
        : [];

      setSetupRows(rows);
      setIsSetupDataReady(true);
    };
    resolveSetupRows();
  }, [isAssembliesLoading, assembliesList]);

  useEffect(() => {
    if (isAssembliesLoading || manufacturersListIsLoading) {
      setFilteredRows({});
      setIsDataReady(false);
      return;
    }
    const resolveFilteredRows = async () => {
      const rows = Object.fromEntries(
        assetsTypeList.map((type) => [
          type,
          Array.isArray(assembliesList)
            ? assembliesList
                .filter(
                  (assembly) =>
                    assembly?.uuid &&
                    assembly.scaleos_type === type &&
                    (assembly.power_capacity !== null ||
                      ["Utility", "Controller"].includes(assembly.scaleos_type)),
                )
                .map((assembly) => {
                  const manufacturer = manufacturersList.find(
                    (m) => m.uuid === assembly.manufacturer,
                  );
                  const modelName =
                    manufacturer?.model?.find((model) => model.uuid === assembly.model)?.name ||
                    null;

                  return {
                    id: assembly.uuid,
                    name: assembly.name || "Unknown",
                    source: assembly.source_info?.[0]?.source || "N/A",
                    lastUpdate:
                      assembly.last_update && dayjs(assembly.last_update).isValid()
                        ? dayjs(assembly.last_update).format("MMM D YYYY, h:mm A")
                        : assembly.last_update,
                    scaleosName: assembly.scaleos_name,
                    manufacturer: assembly.manufacturer,
                    manufacturerName: manufacturer?.name || null,
                    model: assembly.model,
                    modelName,
                    outputCapacity: assembly.power_capacity,
                    storageCapacity: assembly.energy_capacity,
                    scaleosType: assembly.scaleos_type,
                    serialNumber: assembly.serial_number,
                    solarMounting: assembly.mounting,
                    showInScale: assembly.weight,
                    enableAlarms: assembly.trigger_alarms,
                    gensetUse: assembly.genset_use,
                    runHoursLimit: assembly.run_hours_limit,
                    sourceInfo: assembly.source_info,
                    sources: assembly.sources,
                    edgeId: edgeId,
                    parentAsset: assembly.parent_asset ?? null,
                    timeSeries: assembly.time_series || [],
                    sourceName: assembly.source_name,
                  };
                })
            : [],
        ]),
      );

      setFilteredRows(rows);
      setIsDataReady(true);
    };

    resolveFilteredRows();
  }, [isAssembliesLoading, manufacturersListIsLoading, assembliesList, manufacturersList]);

  const columns = [
    { field: "name", headerName: "Asset Name", flex: 1 },
    {
      field: "source",
      headerName: "Source",
      flex: 1,
      renderCell: ({ row }) => row.source + (row.sources?.length > 1 ? " ..." : "") || "N/A",
    },
    {
      field: "lastUpdate",
      headerName: "Available Since",
      flex: 1,
    },
    {
      field: "action",
      headerName: "",
      flex: 0.1,
      align: "right",
      sortable: false,
      renderCell: (params) => <ChevronRightIcon />,
    },
  ];

  const columnsReadyAssets = [
    { field: "scaleosName", headerName: "Scale Name", flex: 1 },
    { field: "manufacturerName", headerName: "Mfr.", flex: 1 },
    { field: "modelName", headerName: "Model", flex: 1 },
    { field: "outputCapacity", headerName: "Capacity (kW)", flex: 1 },
    {
      field: "source",
      headerName: "Source",
      flex: 0.75,
      renderCell: ({ row }) => row.source + (row.sources?.length > 1 ? " ..." : "") || "N/A",
    },

    {
      field: "action",
      headerName: "",
      flex: 0.1,
      align: "right",
      sortable: false,
      renderCell: (params) => <ChevronRightIcon />,
    },
  ];

  const columnsUtilityAssets = [
    { field: "scaleosName", headerName: "Scale Name", flex: 1 },
    {
      field: "source",
      headerName: "Source",
      flex: 1,
      renderCell: ({ row }) => row.source + (row.sources?.length > 1 ? " ..." : "") || "N/A",
    },
    {
      field: "action",
      headerName: "",
      flex: 0.1,
      align: "right",
      sortable: false,
      renderCell: (params) => <ChevronRightIcon />,
    },
  ];

  const handleRowClick = (params) => {
    setSelectedAsset(params.row);
    setIsAssetModalOpen(true);
  };

  // Build grouped props
  const formGroup = useSiteFormState({
    values: {
      name,
      address1,
      city,
      usState,
      zip,
      country,
      timezone,
      latitude,
      longitude,
      siteState,
      siteNotifications,
      contractType,
      dealStructure,
      billSavingsProvider,
      gridServicesProvider,
      commissionedDate,
      contractEndDate,
      dateAcquired,
      sgipBudgetCategory,
      sgipStartDate,
      sgipCapacity,
      utility,
      gasUtility,
      iso,
      postTariffSame,
    },
    set: {
      setName: (v) => {
        setName(v);
        setDirty(true);
      },
      setAddress1: (v) => {
        setAddress1(v);
        setDirty(true);
      },
      setCity: (v) => {
        setCity(v);
        setDirty(true);
      },
      setUsState: (v) => {
        setUsState(v);
        setDirty(true);
      },
      setZip: (v) => {
        setZip(v);
        setDirty(true);
      },
      setCountry: (v) => {
        setCountry(v);
        setDirty(true);
      },
      setTimezone: (v) => {
        setTimezone(v);
        setDirty(true);
      },
      setLatitude: (v) => {
        setLatitude(v);
        setDirty(true);
      },
      setLongitude: (v) => {
        setLongitude(v);
        setDirty(true);
      },
      setSiteState: (v) => {
        setSiteState(v);
        setDirty(true);
      },
      setSiteNotifications: (v) => {
        setSiteNotifications(v);
        setDirty(true);
      },
      setContractType: (v) => {
        setContractType(v);
        setDirty(true);
      },
      setDealStructure: (v) => {
        setDealStructure(v);
        setDirty(true);
      },
      setBillSavingsProvider: (v) => {
        setBillSavingsProvider(v);
        setDirty(true);
      },
      setGridServicesProvider: (v) => {
        setGridServicesProvider(v);
        setDirty(true);
      },
      setCommissionedDate: (v) => {
        setCommissionedDate(v);
        setDirty(true);
      },
      setContractEndDate: (v) => {
        setContractEndDate(v);
        setDirty(true);
      },
      setDateAcquired: (v) => {
        setDateAcquired(v);
        setDirty(true);
      },
      setSgipBudgetCategory: (v) => {
        setSgipBudgetCategory(v);
        setDirty(true);
      },
      setSgipStartDate: (v) => {
        setSgipStartDate(v);
        setDirty(true);
      },
      setSgipCapacity: (v) => {
        setSgipCapacity(v);
        setDirty(true);
      },
      setUtility: (v) => {
        setUtility(v);
        setDirty(true);
      },
      setGasUtility: (v) => {
        setGasUtility(v);
        setDirty(true);
      },
      setIso: (v) => {
        setIso(v);
        setDirty(true);
      },
      setPostTariffSame: (v) => {
        setPostTariffSame(v);
        setDirty(true);
      },
    },
    helpers: { dirty, setDirty, allFieldsFilled },
    lists: {
      statesOptions,
      timezonesOptions,
      sgipOptions,
      utilityList,
      gasUtilitiesList,
      gasUtilitiesListIsLoading,
      isoList,
    },
  });

  const assetsGroup = useAssetsData({
    values: {
      solarCapacity,
      batteryCapacity,
      batteryEnergyCapacity,
      gensetCapacity,
      edgeId,
      hasLoad,
    },
    set: {
      setSolarCapacity: (v) => {
        setSolarCapacity(v);
        setDirty(true);
      },
      setBatteryCapacity: (v) => {
        setBatteryCapacity(v);
        setDirty(true);
      },
      setBatteryEnergyCapacity: (v) => {
        setBatteryEnergyCapacity(v);
        setDirty(true);
      },
      setGensetCapacity: (v) => {
        setGensetCapacity(v);
        setDirty(true);
      },
      setEdgeId: (v) => {
        setEdgeId(v);
        setDirty(true);
      },
      setHasLoad: (v) => {
        setHasLoad(v);
        setDirty(true);
      },
    },
    tables: { columns, columnsReadyAssets, columnsUtilityAssets, filteredRows, setupRows },
    flags: {
      isAssembliesLoading,
      manufacturersListIsLoading,
      isSetupDataReady,
      isDataReady,
      siteSpec4,
    },
    handlers: {
      handleRowClick,
      onOpenPreFill: () => {
        setIsPreFilledModalOpen(true);
      },
    },
    lists: {},
  });

  return (
    <Box>
      <Box mb="1.5rem">
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {currentSite?.name || "New Site"}
          </Typography>
          {currentSite?.uuid && (
            <Box
              display="flex"
              alignItems="center"
              onClick={handleClick}
              sx={{ cursor: "pointer" }}
            >
              <NorthWestOutlined fontSize="small" color="primary" sx={{ mr: 0.5 }} />
              <Typography variant="body2" color="primary">
                Site Overview
              </Typography>
            </Box>
          )}
        </Box>
        {!hideTabs && (
          <>
            <Tabs
              value={activeTab}
              onChange={(e, val) => setActiveTab(val)}
              sx={{ mt: 2 }}
              variant="scrollable"
              scrollButtons={false}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab
                label="Site Details"
                value="details"
                sx={{
                  opacity: 0.7,
                  color: "text.primary",
                  "&&.Mui-selected": { opacity: 1, color: theme.palette.primary.main },
                }}
              />
              {currentSite?.uuid && (
                <Tab
                  label="Assets"
                  value="assets"
                  sx={{
                    opacity: 0.7,
                    color: "text.primary",
                    "&&.Mui-selected": { opacity: 1, color: theme.palette.primary.main },
                  }}
                />
              )}
              {currentSite?.uuid && (
                <Tab
                  label="Integrations"
                  value="integrations"
                  sx={{
                    opacity: 0.7,
                    color: "text.primary",
                    "&&.Mui-selected": { opacity: 1, color: theme.palette.primary.main },
                  }}
                />
              )}
              {currentSite?.uuid && (
                <Tab
                  label="Alarm Actions"
                  value="alarms"
                  sx={{
                    opacity: 0.7,
                    color: "text.primary",
                    "&&.Mui-selected": { opacity: 1, color: theme.palette.primary.main },
                  }}
                />
              )}
            </Tabs>
            {/*Negative to align with tab indicators.*/}
            <Box sx={{ border: "1px solid #E3E3E3", mt: "-2px" }} />
          </>
        )}
      </Box>
      <Box sx={currentSite?.uuid && theme.classes.genericPanel}>
        {activeTab === "details" && (
          <SiteInfo
            styles={styles}
            theme={theme}
            currentSite={currentSite}
            form={formGroup.form}
            lists={formGroup.lists}
            getCountries={getCountries}
            getStatus={getStatus}
            getContractTypes={getContractTypes}
            getDealStructures={getDealStructures}
            getbillSavingsProviders={getbillSavingsProviders}
            getServicesProviders={getServicesProviders}
            isLoading={isLoading}
            error={error}
            onNavigateToIntegrations={() => setActiveTab("integrations")}
          />
        )}
        {activeTab === "assets" && currentSite?.uuid && !isAssembliesLoading && (
          <SiteAssets
            styles={styles}
            theme={theme}
            currentSite={currentSite}
            getPowerOutput={getPowerOutput}
            getEnergyOutput={getEnergyOutput}
            assets={assetsGroup.assets}
            tables={assetsGroup.tables}
            flags={assetsGroup.flags}
            handlers={assetsGroup.handlers}
            lists={assetsGroup.lists}
            assetsTypeList={assetsTypeList}
          />
        )}
        {activeTab === "assets" && selectedAsset && (
          <AssetSetupModal
            asset={selectedAsset}
            site={currentSite}
            siteAssets={assembliesList || []}
            preFill={false}
            isOpen={isAssetModalOpen}
            onClose={() => {
              setIsAssetModalOpen(false);
            }}
            onSave={() => {
              setSelectedAsset(null);
              refetchManufacturersList();
              refetchAssembliesList();
            }}
            index={0}
          />
        )}
        {activeTab === "assets" && setupRows?.length > 0 && (
          <AssetPreFillModal
            assetsList={setupRows}
            site={currentSite}
            siteAssets={assembliesList || []}
            isOpen={isPreFilledModalOpen}
            onClose={() => {
              setIsPreFilledModalOpen(false);
              refetchManufacturersList();
              refetchAssembliesList();
            }}
            onSave={() => {
              refetchManufacturersList();
              refetchAssembliesList();
            }}
          />
        )}
        {activeTab === "assets" && !currentSite?.uuid && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: "-1rem" }}>
            Assets are available after the site is created.
          </Typography>
        )}
        {activeTab === "integrations" && currentSite?.uuid && (
          <SiteIntegrations
            styles={styles}
            theme={theme}
            currentSite={currentSite}
            organizationUuid={OrganizationUuid}
          />
        )}
        {activeTab === "alarms" && currentSite?.uuid && !isAssembliesLoading && (
          <SiteAlarmActions
            panelSx={theme.classes.genericPanel}
            siteUuid={currentSite?.uuid}
            assets={assembliesList}
            upkeepLocationId={currentSite?.upkeep_location_id}
            onNavigateToIntegrations={() => setActiveTab("integrations")}
          />
        )}
        {activeTab === "alarms" && (isAssembliesLoading || !currentSite?.uuid) && (
          <Box sx={theme.classes.genericPanel}>
            <Typography variant="body2" color="textSecondary">
              Loading assets…
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        pt="2rem"
        pb="-1.5rem"
        display="flex"
        flexDirection="row"
        width="100%"
        justifyContent="space-between"
      >
        {currentSite?.uuid ? (
          <Button
            sx={{
              width: "150px",
            }}
            variant="outlined"
            onClick={handleDelete}
            color="error"
          >
            Delete Site...
          </Button>
        ) : (
          <Box flexGrow={1} />
        )}
        <Box>
          {!currentSite?.uuid && (
            <Button
              variant="outlined"
              sx={{
                marginLeft: "1em",
                width: "150px",
                color: "rgba(0, 0, 0, 0.87)",
                borderColor: "rgba(0, 0, 0, 0.87)",
              }}
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
          <LoadingButton
            loading={mutation?.isLoading || mutation_new_site?.isLoading}
            onClick={dirty ? handleSaveAndClose : () => {}}
            variant="contained"
            sx={{ marginLeft: "1em", width: "150px" }}
            disabled={!dirty || (!allFieldsFilled() && !mutation?.isSuccess)}
            color={mutation?.isSuccess && !dirty ? "success" : "primary"}
          >
            {mutation.isSuccess && !dirty
              ? "Saved"
              : currentSite?.uuid
                ? "Save Changes"
                : "Create Site"}
          </LoadingButton>
          <DeleteSiteModal
            open={open}
            onClose={() => setOpen(false)}
            onConfirm={confirmDelete}
            siteName={currentSite?.name}
          />
        </Box>
      </Box>
    </Box>
  );
};

SiteSettings.propTypes = {
  OrganizationUuid: PropTypes.string,
  site: PropTypes.object,
  onClose: PropTypes.func,
  hideTabs: PropTypes.bool,
};

export default SiteSettings;
